import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';

import SettingsContext from 'src/features/administration/context/SettingsContext';
import { PERMISSION } from 'src/features/auth';
import { ALL_CATEGORIES } from 'src/features/membership/utils/constants';
import { PATH } from 'src/features/navigation/utils/constants';
import { useGetFinancialDocumentDescription } from 'src/features/payment/api/useGetFinancialDocumentDescription';
import { useGetOnlinePaymentReceipt } from 'src/features/payment/api/useGetOnlinePaymentReceipt';
import { useGetPayment } from 'src/features/payment/api/useGetPayment';
import { DownloadDigitalReceiptModal } from 'src/features/payment/components/DownloadDigitalReceiptModal';
import { paymentServiceTypeTranslationMap, paymentTypeBadgeOptions } from 'src/features/payment/utils/constants';
import { downloadReceipt, generateCanvas } from 'src/features/payment/utils/digitalReceipt';
import { paymentsMessages } from 'src/features/payment/utils/messages';
import { ReceiptFont } from 'src/features/payment/utils/receiptFont';
import { PAYMENT_ITEM_TYPE, PAYMENT_SOURCE_TYPE, PAYMENT_TYPE } from 'src/features/payment/utils/types';
import { visitorMessages } from 'src/features/visitor/utils/messages';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useParking } from 'src/providers/ParkingProvider';
import GeneralBlockAttribute from 'src/registry/components/blocks/GeneralBlockAttribute';
import {
    SidePanelBodyAttribute,
    SidePanelBodyAttributeBody,
    SidePanelBodyAttributeTitle,
} from 'src/registry/components/sidePanel/SidePanelBody';
import { BadgeOption } from 'src/registry/types/badges';
import { Badge } from 'src/registry/ui/Badge';
import { Button } from 'src/registry/ui/Button';
import {
    DialogBody,
    DialogClose,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { getFormattedDateTime, getFormattedDateTimeForDetailPanel } from 'src/utils/getFormattedDateTime';
import { cn } from 'src/utils/shadcn';

type PaymentSidePanelProps = {
    paymentId?: string;
    dashboardLink?: string;
    accessFromBillingModule?: boolean;
};

const PaymentSidePanel = ({ paymentId, accessFromBillingModule }: PaymentSidePanelProps) => {
    const { formatMessage, formatNumber } = useIntl();
    const settingsData = React.useContext(SettingsContext);

    const { parkingId } = useParking();
    const { data: paymentData } = useGetPayment({
        parkingId,
        paymentId: paymentId ?? '',
        config: {
            enabled: !!paymentId,
        },
    });

    const accessVisitors = useHasPermissions([PERMISSION.ACCESS_VISITORS]);
    const accessActiveStays = useHasPermissions([PERMISSION.ACCESS_ACTIVE_STAYS]);
    const accessPastStays = useHasPermissions([PERMISSION.ACCESS_PAST_STAYS]);
    const accessOperations = useHasPermissions([PERMISSION.ACCESS_PAY_STATION_OPERATIONS]);

    const paymentFinancialDocId = paymentData?.payment_payload_details.financial_document_number;

    const { data: financialDocumentDetails } = useGetFinancialDocumentDescription({
        documentId: paymentFinancialDocId || '',
        parkingId: parkingId,
        config: { enabled: !!paymentFinancialDocId && !!parkingId },
    });

    // An online payment was taken by the cloud, so its fiscal note lives there -
    // with the rows, print attributes and NRA QR the core's financial document has
    // no notion of. The cloud sends the payment order id along as payment_source_id
    // precisely so this screen can ask for it.
    const isOnlinePayment = paymentData?.payment_payload_details?.payment_source_type === PAYMENT_SOURCE_TYPE.ONLINE;
    const onlinePaymentOrderId = isOnlinePayment ? paymentData?.payment_payload_details?.payment_source_id : undefined;

    const { data: onlineReceipt } = useGetOnlinePaymentReceipt({
        parkingId: parkingId,
        paymentOrderId: onlinePaymentOrderId || '',
        config: { enabled: !!onlinePaymentOrderId && !!parkingId },
    });

    const [isLoadedFont, setIsLoadedFont] = React.useState(false);

    React.useEffect(() => {
        let cancelled = false;
        void ReceiptFont.ready().then(() => {
            if (!cancelled) setIsLoadedFont(true);
        });
        return () => {
            cancelled = true;
        };
    }, []);

    if (!paymentData) return null;

    const paymentTypeBadgeOption: BadgeOption = paymentTypeBadgeOptions[paymentData?.type as PAYMENT_TYPE];

    const itemType = paymentData?.payment_payload_details?.item_type as PAYMENT_ITEM_TYPE;
    const itemId = paymentData?.payment_payload_details?.item_id;
    const paymentMembershipCategory = paymentData?.payment_payload_details?.membership_category ?? ALL_CATEGORIES;
    const stayType =
        itemType === PAYMENT_ITEM_TYPE.ACTIVE_STAY
            ? 'active-stay'
            : itemType === PAYMENT_ITEM_TYPE.PAST_STAY
              ? 'past-stay'
              : '';

    const dashboardURLBasedOnItemType =
        itemType == PAYMENT_ITEM_TYPE.MEMBERSHIP
            ? `/${PATH.MEMBERSHIPS}/${paymentMembershipCategory}/${itemId}`
            : `/${PATH.VISITORS}/${stayType}/dashboard/${itemId}`;

    const visitorDisplay = paymentData?.payment_payload_details?.visitor_display;
    const itemDisplay = paymentData?.payment_payload_details?.item_display;

    const receiptNumber = paymentData?.payment_payload_details.receipt_number;
    const sidePanelDescription = receiptNumber ? `#${receiptNumber}` : '';

    const activePastStayPermission = itemType === PAYMENT_ITEM_TYPE.ACTIVE_STAY ? accessActiveStays : accessPastStays;

    // The cloud's note wins where there is one; older online payments predate
    // payment_source_id and fall back to the core's description like everything else.
    const digitalReceiptDetails = onlineReceipt?.details ?? financialDocumentDetails?.public_description ?? '';
    const receiptRows = onlineReceipt?.rows;
    const receiptCharsPerLine = onlineReceipt?.chars_per_line;
    const paymentDate = getFormattedDateTime(paymentData.date)?.formattedDate;
    const payStationOperationId = paymentData?.payment_payload_details?.pay_station_operation_id;
    const payStationOperationRef = paymentData?.payment_payload_details?.pay_station_operation_ref;

    const receiptTextLines = digitalReceiptDetails?.split('\n');
    const fileName = `${formatMessage(paymentsMessages.digitalReceipt)} ${sidePanelDescription} - ${paymentDate}`;
    const handleDownload = () => {
        const canvas = generateCanvas(
            { lines: receiptTextLines, rows: receiptRows, charsPerLine: receiptCharsPerLine },
            isLoadedFont,
        );
        downloadReceipt(canvas, fileName);
    };

    const serviceType = paymentData?.payment_payload_details?.service_type;
    const hasServiceTypeTranslation = Object.hasOwn(paymentServiceTypeTranslationMap, serviceType);

    return (
        <>
            {paymentData ? (
                <DialogHeader>
                    <div className="flex items-center gap-2">
                        <DialogTitle className="pr-0 font-mono">{formatMessage(paymentsMessages.payment)}</DialogTitle>
                    </div>
                    <DialogDescription>{sidePanelDescription}</DialogDescription>
                </DialogHeader>
            ) : (
                <DialogHeader>
                    <Skeleton className="h-7 w-32" />
                    <Skeleton className="h-5 w-60" />
                </DialogHeader>
            )}
            <DialogSeparator />

            <DialogBody>
                <div className="flex aspect-video min-h-44 w-full flex-col items-center justify-center rounded-t-lg bg-gray-100">
                    <FontAwesomeIcon icon={ICON_MAP.RECEIPT} size="2xl" className="opacity-50" />
                </div>
                <DownloadDigitalReceiptModal
                    downloadReceipt={handleDownload}
                    digitalReceiptDetails={digitalReceiptDetails}
                    rows={receiptRows}
                    charsPerLine={receiptCharsPerLine}
                >
                    <DialogTrigger asChild>
                        <Button variant="outline" className="w-full cursor-pointer" disabled={!digitalReceiptDetails}>
                            <FormattedMessage {...paymentsMessages.viewReceipt} />
                        </Button>
                    </DialogTrigger>
                </DownloadDigitalReceiptModal>
                <span className="flex flex-col gap-1">
                    <GeneralBlockAttribute
                        label={formatMessage(visitorMessages.visitor)}
                        value={paymentData?.payment_payload_details?.visitor_display ?? '-'}
                        link={
                            accessVisitors && visitorDisplay
                                ? `/${PATH.VISITORS}/visitor/${paymentData?.payment_payload_details?.visitor_id}`
                                : undefined
                        }
                        icon={visitorDisplay ? ICON_MAP.ARROW_UP_RIGHT : undefined}
                    />

                    <GeneralBlockAttribute
                        label={formatMessage(paymentsMessages.serviceType)}
                        value={
                            hasServiceTypeTranslation
                                ? formatMessage(paymentServiceTypeTranslationMap[serviceType])
                                : (serviceType ?? '-')
                        }
                    />

                    {(itemType === PAYMENT_ITEM_TYPE.ACTIVE_STAY || itemType === PAYMENT_ITEM_TYPE.PAST_STAY) && (
                        <GeneralBlockAttribute
                            label={
                                accessFromBillingModule
                                    ? formatMessage(paymentsMessages.serviceColumnTitle)
                                    : formatMessage(visitorMessages.parkingStay)
                            }
                            value={paymentData?.payment_payload_details?.item_display ?? '-'}
                            link={activePastStayPermission && itemDisplay ? dashboardURLBasedOnItemType : undefined}
                            icon={itemDisplay ? ICON_MAP.ARROW_UP_RIGHT : undefined}
                        />
                    )}
                    {(itemType === PAYMENT_ITEM_TYPE.MEMBERSHIP || itemType === PAYMENT_ITEM_TYPE.SUBSCRIPTION) && (
                        <GeneralBlockAttribute
                            label={formatMessage(paymentsMessages.serviceColumnTitle)}
                            value={paymentData?.payment_payload_details?.item_display ?? '-'}
                            link={itemDisplay ? dashboardURLBasedOnItemType : undefined}
                            icon={itemDisplay ? ICON_MAP.ARROW_UP_RIGHT : undefined}
                        />
                    )}

                    <GeneralBlockAttribute
                        label={formatMessage(paymentsMessages.paidOn)}
                        value={getFormattedDateTimeForDetailPanel(paymentData?.date)}
                    />
                    <GeneralBlockAttribute
                        label={formatMessage(paymentsMessages.receiptNumber)}
                        value={
                            paymentData?.payment_payload_details?.receipt_number
                                ? `#${paymentData?.payment_payload_details?.receipt_number}`
                                : '-'
                        }
                    />

                    <SidePanelBodyAttribute className="flex flex-col gap-0">
                        <SidePanelBodyAttributeTitle>
                            {formatMessage(paymentsMessages.paymentType)}
                        </SidePanelBodyAttributeTitle>
                        <SidePanelBodyAttributeBody control={paymentData?.type} fallbackMessage="-" className="text-sm">
                            <Badge
                                theme={paymentTypeBadgeOption?.theme}
                                variant="tag"
                                className="gap-1.5 whitespace-nowrap"
                            >
                                <FontAwesomeIcon
                                    icon={paymentTypeBadgeOption?.icon as IconProp}
                                    className={cn(
                                        'z-0',
                                        !paymentTypeBadgeOption?.color &&
                                            !paymentTypeBadgeOption?.theme &&
                                            'opacity-50',
                                    )}
                                />
                                {paymentTypeBadgeOption?.label ? formatMessage(paymentTypeBadgeOption?.label) : '-'}
                            </Badge>
                        </SidePanelBodyAttributeBody>
                    </SidePanelBodyAttribute>

                    <GeneralBlockAttribute
                        label={formatMessage(paymentsMessages.amount)}
                        value={
                            paymentData?.amount
                                ? formatNumber(paymentData?.amount / 100, {
                                      style: 'currency',
                                      currency: paymentData?.currency ?? settingsData.default_currency,
                                  })
                                : '-'
                        }
                    />
                    <GeneralBlockAttribute
                        label={formatMessage(paymentsMessages.paidOn)}
                        value={paymentData?.payment_payload_details?.payment_source_display ?? '-'}
                    />

                    {paymentData?.payment_payload_details?.payment_source_type === PAYMENT_SOURCE_TYPE.OPERATOR && (
                        <GeneralBlockAttribute
                            label={formatMessage(paymentsMessages.operator)}
                            value={paymentData?.payment_payload_details?.payment_source_display ?? '-'}
                        />
                    )}
                    <GeneralBlockAttribute
                        label={formatMessage(paymentsMessages.operation)}
                        value={payStationOperationRef ?? '-'}
                        link={
                            accessOperations && payStationOperationId
                                ? `/${PATH.PAYMENTS}/operation/${payStationOperationId}`
                                : undefined
                        }
                        icon={payStationOperationId ? ICON_MAP.ARROW_UP_RIGHT : undefined}
                    />
                </span>
            </DialogBody>

            <DialogFooter>
                <DialogClose asChild>
                    <Button variant="outline" className="flex-1">
                        {formatMessage(generalMessages.close)}
                    </Button>
                </DialogClose>
            </DialogFooter>
        </>
    );
};

export default PaymentSidePanel;
